import React, { useState, useEffect } from 'react';
import useApi from '../../helpers/TerserahApi';
import { PageArea, Fake } from './style';
import { PageContainer, SaveSearch, SEO } from '../../components';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdItem } from '../../components';
import { useTranslation } from 'react-i18next';
import CategoryIcon from '../../components/CategoryIcon';
import SearchWithAutocomplete from '../../components/SearchWithAutocomplete';
import RelatedSearches from '../../components/RelatedSearches';
import { AdGridSkeleton } from '../../components/LoadingSkeleton';
import EmptyState from '../../components/EmptyState';

let timer;

const Page = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const useQueryString = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQueryString();

  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [state, setState] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [condition, setCondition] = useState('');
  const [listingType, setListingType] = useState('');
  const [sortBy, setSortBy] = useState('desc');
  const [stateList, setStateList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [adList, setAdList] = useState([]);
  const [adsTotal, setAdsTotal] = useState(0);
  const [pagination, setPagination] = useState([]);
  const [lastPageDisplay, setLastPageDisplay] = useState(false);
  const [nextPageDisplay, setNextPageDisplay] = useState(false);
  const [currentPage, setCurrentPage] = useState(null);
  const [resultOpacity, setResultOpacity] = useState(1);
  const [warningMessage, setWarningMessage] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [relatedSearches, setRelatedSearches] = useState([]);

  const getAdsList = async () => {
    setError(false);
    setAdList([]);

    const LIMIT = 15;
    const offset = currentPage ? (currentPage - 1) * LIMIT : 0;

    const json = await api.getAds({
      sort: sortBy,
      limit: LIMIT,
      q,
      cat,
      state,
      priceMin: minPrice,
      priceMax: maxPrice,
      condition,
      ...(listingType && { listing_type: listingType }),
      offset,
    });
    if (json.error) {
      const jsonErrors = [];
      for (let err in json.error) {
        jsonErrors.push(json.error[err].msg);
      };

      setError(true);
      setWarningMessage(jsonErrors.join(', '));
    } else {
      setAdList(json.ads);
      setAdsTotal(json.total);
      setResultOpacity(1);
      setWarningMessage('');
      setError(false);
    };

    // Fetch related searches if there's a query
    if (q && q.trim().length >= 2) {
      const relatedJson = await api.getRelatedSearchTerms(q, 8);
      if (!relatedJson.error && relatedJson.relatedTerms) {
        setRelatedSearches(relatedJson.relatedTerms);
      }
    } else {
      setRelatedSearches([]);
    }
  };

  useEffect(() => {
    setAdList([]);
  }, [])

  useEffect(() => {
    const queryQ = query.get('q');
    const queryCat = query.get('cat');
    const queryState = query.get('state');
    const queryListingType = query.get('listing_type');

    queryQ && setQ(queryQ);
    queryCat && setCat(queryCat);
    queryState && setState(queryState);
    queryListingType && setListingType(queryListingType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getStates = async () => {
      const stateList = await api.getStates();
      setStateList(stateList);
    };
    const getCategories = async () => {
      const stateList = await api.getCategories();
      setCategories(stateList);
      setLoading(false);
    };
    getStates();
    getCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setWarningMessage(` ${t('loading')}...`);
    setPagination([]);

    const queryString = [];
    q && queryString.push(`q=${q}`);
    cat && queryString.push(`cat=${cat}`);
    state && queryString.push(`state=${state}`);
    minPrice && queryString.push(`minPrice=${minPrice}`);
    maxPrice && queryString.push(`maxPrice=${maxPrice}`);
    condition && queryString.push(`condition=${condition}`);
    listingType && queryString.push(`listing_type=${listingType}`);
    sortBy !== 'desc' && queryString.push(`sort=${sortBy}`);

    navigate(`?${queryString.join('&')}`, { replace: true });

    if (timer) {
      clearTimeout(timer);
    };

    timer = setTimeout(getAdsList, 500);
    setCurrentPage(1);
    setResultOpacity(0.5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cat, state, minPrice, maxPrice, condition, listingType, sortBy, navigate]);

  useEffect(() => {
    setLastPageDisplay(false);
    setNextPageDisplay(false);

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    if (adList.length > 0) {
      const pageCount = (Math.ceil(adsTotal / adList.length));
      const pageCountArray = [];

      if (currentPage === 1) {
        for (let i = 1; i <= pageCount && i <= 3; i++) {
          pageCountArray.push(i);
        };
        setNextPageDisplay(true);
      } else if (currentPage > 1 && currentPage < pageCount) {
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageCountArray.push(i);
        };
        setLastPageDisplay(true);
        setNextPageDisplay(true);
      } else if (currentPage === 2 & pageCount === 2) {
        for (let i = currentPage - 1; i <= currentPage; i++) {
          pageCountArray.push(i);
        };
        setLastPageDisplay(true);
      } else {
        for (let i = currentPage - 2; i <= currentPage; i++) {
          pageCountArray.push(i);
        };
        setLastPageDisplay(true);
      };

      setPagination(pageCountArray);
    } else {
      setPagination([]);
    };
  }, [adsTotal, adList, currentPage]);

  useEffect(() => {
    if(currentPage) {
      setResultOpacity(0.5);
      getAdsList();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <>
      <SEO
        title={q ? `${q} - Search Results | Terserah` : 'Browse All Ads | Terserah Marketplace'}
        description={q ? `Search results for "${q}" on Terserah. Browse ${adsTotal} listings.` : `Browse ${adsTotal} listings on Terserah - Indonesia's #1 marketplace.`}
      />
      <PageContainer>
        <PageArea
          resultOpacity={resultOpacity}
          lastPageDisplay={lastPageDisplay}
          nextPageDisplay={nextPageDisplay}
        >
          <div className="leftSide">
            <form method="GET">
              <SearchWithAutocomplete
                initialValue={q}
                placeholder={t('whatareYouLookingFor')}
                onSearch={(term) => setQ(term)}
              />

              <div className="filterName">{t('state')}:</div>
              <select name="state" value={state} onChange={e => setState(e.target.value)}>
                <option>{t('selectState')}</option>
                {stateList.map((item) => (
                  <option key={item.uf} value={item.uf}>{item.name}</option>
                ))}
              </select>

              <div className="filterName">{t('Price Range')}:</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                <input
                  type="number"
                  placeholder={t('Min')}
                  value={minPrice}
                  onChange={e => setMinPrice(e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
                <input
                  type="number"
                  placeholder={t('Max')}
                  value={maxPrice}
                  onChange={e => setMaxPrice(e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                />
              </div>

              <div className="filterName">{t('Condition')}:</div>
              <select name="condition" value={condition} onChange={e => setCondition(e.target.value)} style={{ marginBottom: '15px' }}>
                <option value="">{t('All')}</option>
                <option value="new">{t('condition_new')}</option>
                <option value="like_new">{t('condition_like_new')}</option>
                <option value="good">{t('condition_good')}</option>
                <option value="fair">{t('condition_fair')}</option>
                <option value="poor">{t('condition_poor')}</option>
              </select>

              <div className="filterName">{t('Type')}:</div>
              <select name="listing_type" value={listingType} onChange={e => setListingType(e.target.value)} style={{ marginBottom: '15px' }}>
                <option value="">{t('All')}</option>
                <option value="sale">For Sale</option>
                <option value="rent">For Rent</option>
              </select>

              <div className="filterName">
                {(() => {
                  const selectedCategory = categories.find(c => c.slug === cat);
                  if (selectedCategory) {
                    return t('Subcategory');
                  }
                  return t('category');
                })()}:
              </div>
              <ul>
                {loading ? <Fake height="200" fillContent/>
                : (() => {
                    // Find if the current cat matches a category by slug
                    const selectedCategory = categories.find(c => c.slug === cat);
                    
                    if (selectedCategory) {
                      // If it's a main category, show its subcategories
                      if (!selectedCategory.parent_id) {
                        const subcategories = categories.filter(c => c.parent_id === selectedCategory.id);
                        if (subcategories.length > 0) {
                          return (
                            <>
                              <li 
                                className="categoryItem"
                                onClick={() => setCat('')}
                                style={{ borderBottom: '2px solid #ccc', paddingBottom: '10px', marginBottom: '10px', cursor: 'pointer' }}
                              >
                                <span>← {t('All Categories')}</span>
                              </li>
                              {subcategories.map((item) => (
                                <li
                                  key={item.slug}
                                  className={`categoryItem ${cat === item.slug && 'active'}`}
                                  onClick={() => setCat(item.slug)}
                                >
                                  {item.icon && <CategoryIcon icon={item.icon} size={20} categoryId={item.id} style={{ marginRight: '8px' }} />}
                                  <span>{t(item.name)}</span>
                                </li>
                              ))}
                            </>
                          );
                        }
                      } else {
                        // If it's a subcategory, show all subcategories from the same parent
                        const parentCategory = categories.find(c => c.id === selectedCategory.parent_id);
                        if (parentCategory) {
                          const siblingSubcategories = categories.filter(c => c.parent_id === parentCategory.id);
                          return (
                            <>
                              <li 
                                className="categoryItem"
                                onClick={() => setCat(parentCategory.slug)}
                                style={{ borderBottom: '2px solid #ccc', paddingBottom: '10px', marginBottom: '10px', cursor: 'pointer' }}
                              >
                                <span>← {t(parentCategory.name)}</span>
                              </li>
                              {siblingSubcategories.map((item) => (
                                <li
                                  key={item.slug}
                                  className={`categoryItem ${cat === item.slug && 'active'}`}
                                  onClick={() => setCat(item.slug)}
                                >
                                  {item.icon && <i className={`fas ${item.icon}`} style={{ marginRight: '8px' }}></i>}
                                  <span>{t(item.name)}</span>
                                </li>
                              ))}
                            </>
                          );
                        }
                      }
                    }
                    
                    // Default: show all main categories
                    return categories
                      .filter(item => !item.parent_id)
                      .map((item) => (
                        <li
                          key={item.slug}
                          className={`categoryItem ${cat === item.slug && 'active'}`}
                          onClick={() => setCat(item.slug)}
                        >
                          {item.icon && <CategoryIcon icon={item.icon} size={24} categoryId={item.id} style={{ marginRight: '8px' }} />}
                          <span>{t(item.name)}</span>
                        </li>
                      ));
                  })()
                }
              </ul>
            </form>
          </div>
          <div className="rightSide">
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ margin: 0 }}>{t('Results')} ({adsTotal})</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select 
                  value={sortBy} 
                  onChange={e => setSortBy(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '14px' }}
                >
                  <option value="desc">{t('Newest First')}</option>
                  <option value="asc">{t('Oldest First')}</option>
                  <option value="price_asc">{t('Price: Low to High')}</option>
                  <option value="price_desc">{t('Price: High to Low')}</option>
                  <option value="views">{t('Most Viewed')}</option>
                </select>
                {(q || cat || state || minPrice || maxPrice || condition) && <SaveSearch />}
              </div>
            </div>
            {warningMessage && <div className="listWarning">{warningMessage}</div>}
            {error === false &&
              <div className="list">
                {loading ? (
                  <AdGridSkeleton count={9} />
                ) : adList.length > 0 ? (
                  adList.map((item) => (
                    <AdItem key={item.public_id} data={item} />
                  ))
                ) : (
                  <EmptyState 
                    type="no-ads"
                    actionText={t('Clear filters')}
                    actionLink="/ads"
                  />
                )}
              </div>
            }
            {pagination.length > 1 &&
              <div className="pagination">
                <div onClick={() => lastPageDisplay && setCurrentPage(currentPage - 1)}
                  className='pagItem lastPage'
                  aria-label={t('previousPage')}
                >
                  &lt;
                  </div>
                {pagination.map((item) => (
                  <div
                    onClick={() => setCurrentPage(item)}
                    className={item === currentPage ? 'pagItem active' : 'pagItem'}
                    key={item}
                  >
                    {item}
                  </div>
                ))}
                <div
                  onClick={() => nextPageDisplay && setCurrentPage(currentPage + 1)}
                  className='pagItem nextPage'
                  aria-label={t('nextPage')}
                >
                  &gt;
                </div>
              </div>
            }

            {/* Related Searches */}
            {relatedSearches.length > 0 && (
              <RelatedSearches searches={relatedSearches} currentQuery={q} />
            )}
          </div>
        </PageArea>
      </PageContainer>
    </>
  );
};

export default Page;